import React, { useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { LoadingComponent } from "../../../Components/Loading";
import { ModalComponent } from "../../../Components/Modal";
import { ProcessingPaymentComponent } from "./PaymentComponents/ProcessingPaymentComponent";
import { useTranslation } from "react-i18next";
import { BiCreditCard } from "react-icons/bi";

export const RedsysRedirectionButtonComponent = ({
  id,
  description,
  name,
  shippingMethod,
  deliveryAt,
  validateOrder,
  cartProducts,
  user,
  comments,
  couponCode,
  shippingAddress,
}) => {
  const redsysRef = useRef(null);
  const [show, setShow] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [wooOrder, setWooOrder] = useState(null);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  async function initRedsys() {
    setLoading(true);
    setIframeLoading(true);
    setError(null);
    try {
      const res = await fetch(
        process.env.REACT_APP_API_HOST + "/api/payment",
        {
          method: "post",
          headers: { "X-Customer-Token": user.access_token },
          body: JSON.stringify({
            products: cartProducts,
            payment_method: String(id),
            shipping_method: shippingMethod,
            delivery_at: deliveryAt,
            coupon_code: couponCode,
            comments: comments,
            twc_woo_order_id: wooOrder,
            shipping_address: shippingAddress,
          }),
        }
      );
      if (res.status !== 200) throw new Error("Payment init failed");
      const { data } = await res.json();

      const form = redsysRef.current;
      form.action = data.url;
      form.querySelector('input[name="Ds_SignatureVersion"]').value =
        data.signature_version;
      form.querySelector('input[name="Ds_Signature"]').value = data.signature;
      form.querySelector('input[name="Ds_MerchantParameters"]').value =
        data.merchant_parameters;
      form.submit();
      setTransaction(data.transaction);
      setWooOrder(data.twc_woo_order_id);
    } catch (err) {
      console.error(err);
      setError(t("No se pudo conectar con la pasarela de pago. Inténtalo de nuevo."));
    } finally {
      setLoading(false);
    }
  }

  const handleClick = async (e) => {
    const validationResult = validateOrder();
    switch (validationResult) {
      case "missing_shipping_method":
        alert(t("Falta seleccionar forma de entrega"));
        return;
      case "missing_shipping_delivery_at":
        alert(t("Selecciona una fecha de entrega"));
        return;
      case "missing_shipping_address":
        alert(t("Selecciona una dirección de envío"));
        return;
      default:
        setSubmitting(true);
        setError(null);
        setShow(true);
        await initRedsys();
        setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="dark"
        size="lg"
        className="w-100 rounded-pill py-3 d-flex align-items-center justify-content-center gap-2"
        title={description}
        onClick={handleClick}
      >
        <BiCreditCard size={22} />
        {name}
      </Button>

      <RedsysFormComponent forwardRef={redsysRef} />

      <ModalComponent
        title={t("Completa tu pago")}
        show={show}
        setShow={setShow}
        size="lg"
      >
        <div className="text-center py-2">
          {loading && (
            <LoadingComponent
              loadingMessages={[
                t("Cargando la pasarela de pago..."),
                t("Conectando con el banco..."),
                t("Ya casi está, un momento..."),
              ]}
            />
          )}

          {error && !loading && (
            <div className="py-4">
              <div
                className="d-flex align-items-center justify-content-center mb-3"
                style={{ color: "var(--tws-danger)" }}
              >
                <span style={{ fontSize: "48px" }}>&#9888;</span>
              </div>
              <h5 className="fw-bold mb-2">{t("El pago ha sido rechazado")}</h5>
              <p className="text-muted mb-4">{error}</p>
              <Button
                variant="dark"
                className="rounded-pill px-4"
                onClick={async () => {
                  setError(null);
                  await initRedsys();
                }}
              >
                {t("Volver a intentarlo")}
              </Button>
            </div>
          )}

          <div
            className="position-relative"
            style={{
              aspectRatio: "3/4",
              maxHeight: "70vh",
              display: loading || error ? "none" : "block",
              width: "100%",
            }}
          >
            {iframeLoading && !loading && (
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white rounded">
                <div className="text-center">
                  <Spinner animation="border" className="mb-2" />
                  <p className="small text-muted mb-0">
                    {t("Cargando formulario de pago...")}
                  </p>
                </div>
              </div>
            )}
            <iframe
              title="Redsys Payment"
              name="redsys-redirection-iframe"
              className="w-100 h-100 border-0 rounded"
              onLoad={() => setIframeLoading(false)}
            />
          </div>
        </div>
      </ModalComponent>

      {transaction && (
        <ProcessingPaymentComponent
          transaction={transaction}
          setError={setError}
          setTransaction={setTransaction}
        />
      )}
    </>
  );
};

const RedsysFormComponent = ({ forwardRef }) => (
  <form
    ref={forwardRef}
    target="redsys-redirection-iframe"
    name="redsys-form"
    action=""
    method="POST"
    style={{ display: "none" }}
  >
    <input type="hidden" name="Ds_SignatureVersion" value="" />
    <input type="hidden" name="Ds_MerchantParameters" value="" />
    <input type="hidden" name="Ds_Signature" value="" />
  </form>
);
